import {
  getRates,
  getQuoteMetaFromCode,
  getInstantQuoteDefault,
} from '../../Services/api.jsx'
import * as domBridge from '../../Services/domBridge.jsx'
import * as storage from '../../Services/storage.jsx'
import * as Gtm from '../../Services/gtm.jsx'
import {experimentTracker} from '../../Services/tracker.jsx'
import {features} from '../../Services/features.jsx'
import {
  getQuoteCodeFromUrl,
  getBrowserTimeZone,
  findNearTimeZone,
} from '../../Services/helpers.jsx'

const isDevelopment = ['test', 'development'].includes(process.env.NODE_ENV)

export function useInstantQuoteInitialization({
  languagesState,
  subjects,
  sourceLanguage,
  targetLanguages,
  subject,
  currency,
  selectedTimeZone,
  dispatchForm,
  dispatchConfig,
  onChangeCurrency,
  handleSubmit,
  setWordCount,
  removeFiles,
  files,
}) {
  const getMountedRef = () => {
    let mounted = true
    return {
      get: () => mounted,
      set: (value) => {
        mounted = value
      },
    }
  }
  const initializeRates = async (mountedRef) => {
    const ratesData = await getRates()
    if (mountedRef.get()) {
      dispatchConfig({type: 'SET_RATES', payload: ratesData})
    }
  }

  const initializeSolutions = async (mountedRef) => {
    const [firstCard, secondCard, thirdCard] = await Promise.all([
      features.getFirstCardSolution(),
      features.getSecondCardSolution(),
      features.getThirdCardSolution(),
    ])
    if (mountedRef.get()) {
      dispatchConfig({
        type: 'SET_SOLUTIONS',
        payload: {first: firstCard, second: secondCard, third: thirdCard},
      })
    }
  }

  const initializeCurrency = () => {
    //tofix: wait for currency select to be initalised with localStorage value if any
    setTimeout(() => {
      const currencyValue = domBridge.getSelectedCurrency()
      onChangeCurrency(currencyValue)
    }, 50)
    domBridge.currencyChangeAddEventListener(onChangeCurrency)
  }

  const initializeTimezone = (mountedRef) => {
    const currentTimezone = storage.sessionGetCurrentTimezone()
    if (currentTimezone && mountedRef.get()) {
      dispatchConfig({type: 'SET_TIMEZONE', payload: currentTimezone})
    }
    return currentTimezone
  }

  const initializeFromQuoteCode = async (
    quoteCode,
    currentTimezone,
    mountedRef,
  ) => {
    if (!quoteCode.length) return

    const quoteMeta = await getQuoteMetaFromCode(
      quoteCode,
      languagesState,
      currentTimezone || getBrowserTimeZone(),
    )

    const currentSubject = quoteMeta.subject
      ? subjects.find((el) => el.id == quoteMeta.subject)
      : undefined

    if (mountedRef.get()) {
      dispatchForm({
        type: 'SET_SOURCE_LANGUAGE',
        payload: quoteMeta.sourceLanguage || sourceLanguage,
      })
      dispatchForm({
        type: 'SET_TARGET_LANGUAGES',
        payload: quoteMeta.targetLanguages || targetLanguages,
      })
      if (quoteMeta.wordCount) {
        setWordCount(quoteMeta.wordCount)
      }
      dispatchForm({type: 'SET_SUBJECT', payload: currentSubject || subject})
      dispatchForm({type: 'SET_DELIVERY_DATE', payload: quoteMeta.deliveryDate})

      // Call onSubmit immediately after state updates
      setTimeout(() => {
        if (mountedRef.get()) {
          handleSubmit()
        }
      }, 0)
    }
  }

  const initializeFromProjectInfo = (shouldUseQueryWordsParam, mountedRef) => {
    const projectInfo = storage.getProjectInfo()
    const shouldInstantQuoteUseLocalStorageParams =
      domBridge.shouldInstantQuoteUseLocalStorageParams()

    if (
      shouldInstantQuoteUseLocalStorageParams &&
      !shouldUseQueryWordsParam &&
      projectInfo &&
      files.length &&
      projectInfo.wordCount
    ) {
      if (mountedRef.get()) {
        setWordCount(projectInfo.wordCount)
      }
    } else if (shouldUseQueryWordsParam) {
      storage.clearProjectInfo()
      if (mountedRef.get()) {
        setTimeout(() => {
          if (mountedRef.get()) {
            handleSubmit()
          }
        }, 0)
      }
    }
  }

  const initializeFromRemoteDefaults = async (mountedRef) => {
    try {
      const response = await getInstantQuoteDefault()
      const sourceLanguageValue = languagesState.find(
        (language) => language.id === response.source,
      )
      const targetLanguageValue = languagesState.find(
        (language) => language.id === response.target,
      )
      const currencyValue = response.currency
      const timeZone = response.timezone
        ? findNearTimeZone(response.timezone)
        : undefined

      if (mountedRef.get()) {
        dispatchForm({
          type: 'SET_SOURCE_LANGUAGE',
          payload: sourceLanguageValue || sourceLanguage,
        })
        dispatchForm({
          type: 'SET_TARGET_LANGUAGES',
          payload: targetLanguageValue
            ? [targetLanguageValue]
            : targetLanguages,
        })
        dispatchConfig({
          type: 'SET_CURRENCY',
          payload: currencyValue || currency,
        })
        dispatchConfig({
          type: 'SET_TIMEZONE',
          payload: timeZone || selectedTimeZone,
        })
      }
    } catch (error) {
      console.log('getInstantQuoteDefault: ', error)
    }
  }

  const initializeFromLocalStorage = (mountedRef) => {
    const storedSourceLanguage = storage.getInstantQuoteSourceLanguage()
    const storedTargetLanguages = storage.getInstantQuoteTargetLanguages()
    const storedSubject = storage.getInstantQuoteSubject()

    let sourceLanguageValue = storedSourceLanguage
      ? languagesState.find(
          (language) => language.id === storedSourceLanguage.id,
        )
      : sourceLanguage

    let targetLanguagesValue = storedTargetLanguages
      ? storedTargetLanguages.map((storedTargetLanguage) =>
          languagesState.find(
            (language) => language.id === storedTargetLanguage.id,
          ),
        )
      : targetLanguages

    // Filter out undefined values in case language mapping changed
    targetLanguagesValue = targetLanguagesValue.filter(Boolean)

    if (
      sourceLanguageValue &&
      targetLanguagesValue.length > 0 &&
      mountedRef.get()
    ) {
      dispatchForm({type: 'SET_SOURCE_LANGUAGE', payload: sourceLanguageValue})
      dispatchForm({
        type: 'SET_TARGET_LANGUAGES',
        payload: targetLanguagesValue,
      })
      dispatchForm({
        type: 'SET_SUBJECT',
        payload: storedSubject
          ? subjects.find((subject) => subject.id === storedSubject.id)
          : subject,
      })
    }
  }

  const trackInitialization = () => {
    setTimeout(() => {
      try {
        !isDevelopment &&
          window.heap &&
          window.heap.track('step-1-instant-quote')
      } catch (error) {
        if (isDevelopment) {
          console.error('Could not track event', error.message)
        }
      }
    }, 500)
  }

  const initializeComponent = async (mountedRef) => {
    Gtm.setFunnelStep(1)
    experimentTracker.quoteCalculatorShown()
    removeFiles(true, false)
    domBridge.hideHtmlStaticInstantQuoteForm()

    await initializeRates(mountedRef)
    await initializeSolutions(mountedRef)
    initializeCurrency()
    const currentTimezone = initializeTimezone(mountedRef)

    const quoteCode = getQuoteCodeFromUrl()
    await initializeFromQuoteCode(quoteCode, currentTimezone, mountedRef)

    const shouldUseQueryWordsParam = domBridge.existsQueryParam('words')
    initializeFromProjectInfo(shouldUseQueryWordsParam, mountedRef)

    const instantQuoteQueryInfoExists =
      !!domBridge.existsQueryParam('source') ||
      !!domBridge.existsQueryParam('target') ||
      !!domBridge.existsQueryParam('subject') ||
      !!domBridge.existsQueryParam('words') ||
      quoteCode.length !== 0
    const instantQuoteLocalStorageInfoExists =
      !!storage.getInstantQuoteSourceLanguage() ||
      !!storage.getInstantQuoteTargetLanguages() ||
      !!storage.getInstantQuoteSubject() ||
      !!storage.getInstantQuoteWords()
    const shouldGetRemoteDefaultInstantQuoteOptions =
      !instantQuoteQueryInfoExists && !instantQuoteLocalStorageInfoExists

    if (shouldGetRemoteDefaultInstantQuoteOptions) {
      await initializeFromRemoteDefaults(mountedRef)
    } else if (domBridge.shouldInstantQuoteUseLocalStorageParams()) {
      initializeFromLocalStorage(mountedRef)
    }

    trackInitialization()
  }

  return {initializeComponent, getMountedRef}
}
